import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { noop } from "lodash";
import { useCallback, useContext, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";
import { ErrorText } from "../../../../components/billing";
import { PrimaryButton } from "../../../../components/button";
import { RadioGroup } from "../../../../components/forms/radio";
import { TextField } from "../../../../components/forms/text";
import { KycIcon } from "../../../../components/icons/status";
import { Flex } from "../../../../components/layout";
import { useAppLoader } from "../../../../components/loader/app-loader-hook";
import { GlobalOverlayContext } from "../../../../components/overlay/context";
import { StatusEnum } from "../../../../store/users/models/internal";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import { ServiceType } from "../../../../store/verification/models/internal";
import { VerificationService } from "../../../../store/verification/service";

type FormType = VerificationDTO.Requests.UpdateKycStatus.T;

const Wrapper = styled(Grid).attrs({ container: true })`
  min-width: 25em;
`;

const Buttons = styled(Flex)`
  justify-content: flex-end;
`;

const CTA = styled(PrimaryButton)`
  & + & {
    margin-left: 1em;
  }
`;

export interface IApprovalModalProps {
  item: VerificationDTO.Responses.GetSingleKyc.T;
  allServices: Array<typeof ServiceType.T>;
}

const statusOptions: Array<StatusEnum> = ["VERIFIED", "DECLINED", "IN_REVIEW"];

export function StatusChangeModal({ item, allServices }: IApprovalModalProps) {
  const { setOverlay } = useContext(GlobalOverlayContext);
  const loader = useAppLoader();
  const router = useRouter();

  const validation = VerificationDTO.Requests.UpdateKycStatus.useFields();

  const { control, handleSubmit } = useForm<FormType>({
    defaultValues: useMemo(
      () => ({
        status: "VERIFIED",
      }),
      []
    ),
    resolver: yupResolver(validation),
  });

  const submit = useCallback(
    (data: FormType) =>
      loader(
        (data.status === "VERIFIED"
          ? VerificationService.updateKycStatuses(item.fraudCheckId, {
              note: data.note,
              serviceDeclineCodes: allServices.reduce(
                (acc, curr) => ({ ...acc, [curr]: [] }),
                {}
              ),
            })
          : VerificationService.updateKycStatus(item.fraudCheckId, data)
        )
          .then(() => {
            router.stateService.reload();
          })
          .catch(noop)
      ),
    [router, item]
  );

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Wrapper spacing={3}>
        <Grid item xs={12}>
          <Flex center>
            <Controller
              control={control}
              name="status"
              render={({ field, fieldState: { error } }) => (
                <>
                  <RadioGroup<any>
                    options={statusOptions
                      .filter((x) => x !== item.userStatus)
                      .map((val) => ({
                        id: val,
                        label: KycIcon(val, "kyc"),
                      }))}
                    {...field}
                  />
                  {error ? <ErrorText>{error.message}</ErrorText> : null}
                </>
              )}
            />
          </Flex>
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="note"
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                {...field}
                placeholder="Add a comment"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Buttons>
            <CTA
              variant="outlined"
              onClick={() =>
                setOverlay({
                  title: "",
                  children: null,
                })
              }
            >
              Cancel
            </CTA>
            <CTA type="submit">Confirm</CTA>
          </Buttons>
        </Grid>
      </Wrapper>
    </form>
  );
}
